export type NavDirection = 'left' | 'right' | 'up' | 'down';

export const ARROW_DIRECTIONS: Readonly<Record<string, NavDirection>> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
};

/**
 * Spatial keyboard navigation between map points: the nearest point in the
 * pressed direction, favoring points close to the straight line. Points at
 * the same position are stepped through in list order (right/down forward,
 * left/up backward) so none is unreachable. Returns null at an edge.
 */
export const findNeighbor = (
  points: ReadonlyArray<{ x: number; y: number }>,
  from: number,
  direction: NavDirection
): number | null => {
  const origin = points[from];
  if (!origin) return null;
  const forward = direction === 'right' || direction === 'down';
  let best: number | null = null;
  let bestScore = Infinity;
  points.forEach((point, index) => {
    if (index === from) return;
    const dx = point.x - origin.x;
    const dy = point.y - origin.y;
    let along: number;
    let across: number;
    switch (direction) {
      case 'right':
        [along, across] = [dx, dy];
        break;
      case 'left':
        [along, across] = [-dx, dy];
        break;
      case 'down':
        [along, across] = [dy, dx];
        break;
      default:
        [along, across] = [-dy, dx];
    }
    let score: number;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
      // Coincident points: walk them in list order.
      if (forward ? index < from : index > from) return;
      score = Math.abs(index - from) * 1e-6;
    } else {
      if (along <= 0) return;
      score = along + 2 * Math.abs(across);
    }
    if (score < bestScore) {
      bestScore = score;
      best = index;
    }
  });
  return best;
};
